// PlayerCv: la "tessera giocatore" condivisibile del player. Mostra una
// sintesi inline (identità, KPI carriera, breakdown stagione/amichevole,
// top moments) + un pulsante "Condividi il mio CV" che apre un dialog con
// la stessa carta in versione PNG brandizzata (logo, gradient, footer),
// scaricabile / copiabile / condivisibile via Web Share API.
//
// La cattura DOM → PNG passa per html-to-image con `skipFonts:true` per
// non far esplodere la CSP, e una preview off-screen "position:fixed
// top:-10000px" così il browser ne fa il layout completo. Vedi anche
// PlayerRoleComparison (stessa tecnica).

import { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { useTranslation } from "react-i18next";
import { Check, Copy, Download, Loader2, Share2, Sparkles, Star, Target, Trophy, X } from "lucide-react";
import { Avatar, AvatarFallback } from "@v2/components/ui/avatar";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@v2/components/ui/dialog";
import { cn } from "@v2/lib/utils";
import { ELEVENBASE_LOGO_URL } from "../../components/brand";
import { currentIntlLocale } from "../../lib/i18n";

export type CvAggregate = {
  games: number;
  goals: number;
  assists: number;
  mom: number;
  ratingAvg: number;
  winPct: number;
  minutes: number;
};

export type CvCareer = {
  // dati EA "all-time" del club (gamesPlayed, goals, ...).
  gamesPlayed: number;
  goals: number;
  assists: number;
  mom: number;
  winRate: number;
  ratingAve: number;
  proName?: string | null;
  proOverall?: number | null;
  favoritePosition?: string | null;
};

export type CvTopMatch = {
  date: string | null;
  opponentName: string;
  ourGoals: number;
  oppGoals: number;
  rating: number;
  goals: number;
  assists: number;
} | null;

export function PlayerCv({
  displayName,
  rolesAffinity,
  eaPersona,
  career,
  season,
  friendly,
  all,
  bestRating,
  bestGoals,
}: {
  displayName: string;
  // Ruolo di rosa del player (POR/DC/TD/...). Opzionale, da etichetta.
  rolesAffinity?: string | null;
  // Gamertag EA o pro name (visibile come "Account").
  eaPersona: string;
  career: CvCareer | null;
  season: CvAggregate;
  friendly: CvAggregate;
  all: CvAggregate;
  bestRating: CvTopMatch;
  bestGoals: CvTopMatch;
}) {
  const { t } = useTranslation("mondofc");
  const shareRef = useRef<HTMLDivElement | null>(null);
  const [shareState, setShareState] = useState<{
    dataUrl: string | null;
    error: string | null;
  } | null>(null);

  const initials = displayName
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const openShare = () => {
    setShareState({ dataUrl: null, error: null });
  };

  // useEffect genera il PNG dal preview off-screen.
  useEffect(() => {
    if (!shareState || shareState.dataUrl || shareState.error) return;
    let cancelled = false;
    const target = shareRef.current;
    if (!target) {
      setShareState((cur) => (cur ? { ...cur, error: "missing_preview" } : cur));
      return;
    }
    (async () => {
      try {
        await waitForImages(target);
        await new Promise<void>((r) => requestAnimationFrame(() => r()));
        await new Promise<void>((r) => requestAnimationFrame(() => r()));
        const rect = target.getBoundingClientRect();
        const width = Math.max(1, Math.round(rect.width));
        const height = Math.max(1, Math.round(rect.height));
        const dataUrl = await toPng(target, {
          width,
          height,
          pixelRatio: 2,
          cacheBust: true,
          backgroundColor: "#0a1114",
          skipFonts: true,
          fontEmbedCSS: "",
          style: {
            width: `${width}px`,
            height: `${height}px`,
            position: "static",
            top: "auto",
            left: "auto",
          },
        });
        if (cancelled) return;
        setShareState((cur) => (cur ? { ...cur, dataUrl } : cur));
      } catch (err) {
        if (cancelled) return;
        console.error("[PlayerCv] toPng failed", err);
        setShareState((cur) =>
          cur ? { ...cur, error: err instanceof Error ? err.message : "unknown" } : cur,
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shareState]);

  return (
    <>
      {/* Carta CV inline mostrata nel tab "My Player". Click su Condividi
          apre il dialog con la versione PNG brandizzata. */}
      <Card className="shadow-card overflow-hidden">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                {t("cv.title")}
              </CardTitle>
              <CardDescription>{t("cv.desc")}</CardDescription>
            </div>
            <Button type="button" size="sm" onClick={openShare}>
              <Share2 className="h-3.5 w-3.5 mr-1.5" />
              {t("cv.shareCta")}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <CvBody
            displayName={displayName}
            initials={initials}
            rolesAffinity={rolesAffinity}
            eaPersona={eaPersona}
            career={career}
            season={season}
            friendly={friendly}
            all={all}
            bestRating={bestRating}
            bestGoals={bestGoals}
            t={t}
          />
        </CardContent>
      </Card>

      {/* Off-screen brandizzato: catturato dal toPng. */}
      <div
        ref={shareRef}
        aria-hidden
        style={{
          position: "fixed",
          top: "-10000px",
          left: 0,
          width: "720px",
          pointerEvents: "none",
          zIndex: -1,
        }}
      >
        <ShareableCv
          displayName={displayName}
          initials={initials}
          rolesAffinity={rolesAffinity}
          eaPersona={eaPersona}
          career={career}
          season={season}
          friendly={friendly}
          all={all}
          bestRating={bestRating}
          bestGoals={bestGoals}
          t={t}
        />
      </div>

      {/* Dialog Condividi */}
      <ShareDialog
        state={shareState}
        title={t("cv.shareTitle")}
        text={t("cv.shareText", { name: displayName })}
        fileName={`elevenbase-${displayName.toLowerCase().replace(/\s+/g, "-")}-cv.png`}
        onClose={() => setShareState(null)}
        t={t}
      />
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Body del CV (versione inline). Riusato anche dalla preview PNG con stili
// adattati (vedi ShareableCv).
// ──────────────────────────────────────────────────────────────────────────

function CvBody({
  displayName,
  initials,
  rolesAffinity,
  eaPersona,
  career,
  season,
  friendly,
  all,
  bestRating,
  bestGoals,
  t,
}: {
  displayName: string;
  initials: string;
  rolesAffinity?: string | null;
  eaPersona: string;
  career: CvCareer | null;
  season: CvAggregate;
  friendly: CvAggregate;
  all: CvAggregate;
  bestRating: CvTopMatch;
  bestGoals: CvTopMatch;
  t: (k: string, opts?: any) => string;
}) {
  return (
    <div className="space-y-4">
      {/* Identità */}
      <div className="flex items-center gap-3">
        <Avatar className="h-14 w-14">
          <AvatarFallback className="text-base font-semibold">{initials}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="text-lg font-bold leading-tight truncate">{displayName}</p>
          <p className="text-xs text-muted-foreground truncate">
            {rolesAffinity ? `${rolesAffinity} · ` : ""}
            <span className="font-mono">{eaPersona}</span>
            {career?.proOverall ? ` · OVR ${career.proOverall}` : ""}
          </p>
          {career?.favoritePosition && (
            <p className="text-[11px] text-muted-foreground">
              {t("cv.favoritePosition")}: {career.favoritePosition}
            </p>
          )}
        </div>
      </div>

      {/* KPI Carriera EA all-time */}
      {career && career.gamesPlayed > 0 && (
        <div className="rounded-xl border bg-card/60 p-3">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
            {t("cv.careerSection")}
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            <KpiTile label={t("cv.kpi.matches")} value={String(career.gamesPlayed)} />
            <KpiTile label={t("cv.kpi.goals")} value={String(career.goals)} tone="primary" />
            <KpiTile label={t("cv.kpi.assists")} value={String(career.assists)} tone="amber" />
            <KpiTile label={t("cv.kpi.rating")} value={career.ratingAve.toFixed(2)} />
            <KpiTile label={t("cv.kpi.winRate")} value={`${career.winRate.toFixed(0)}%`} />
            <KpiTile label={t("cv.kpi.mom")} value={String(career.mom)} />
          </div>
        </div>
      )}

      {/* Breakdown Stagione + Amichevole */}
      <div className="grid sm:grid-cols-2 gap-3">
        <BreakdownCard label={t("cv.section.season")} agg={season} t={t} />
        <BreakdownCard label={t("cv.section.friendly")} agg={friendly} t={t} />
      </div>

      {/* Top match */}
      {(bestRating || bestGoals) && (
        <div className="grid sm:grid-cols-2 gap-3">
          {bestRating && (
            <TopMatchTile
              icon={<Star className="h-3.5 w-3.5 text-amber-500" />}
              title={t("cv.bestRating")}
              match={bestRating}
              metricLabel={t("cv.ratingLabel")}
              metricValue={bestRating.rating.toFixed(2)}
            />
          )}
          {bestGoals && bestGoals.goals > 0 && (
            <TopMatchTile
              icon={<Target className="h-3.5 w-3.5 text-emerald-500" />}
              title={t("cv.bestGoals")}
              match={bestGoals}
              metricLabel={t("cv.goalsLabel")}
              metricValue={`${bestGoals.goals}G`}
            />
          )}
        </div>
      )}
    </div>
  );
}

function KpiTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "primary" | "amber" | undefined;
}) {
  const valueTone =
    tone === "primary"
      ? "text-emerald-600 dark:text-emerald-400"
      : tone === "amber"
        ? "text-amber-600 dark:text-amber-400"
        : "text-foreground";
  return (
    <div className="rounded-lg border bg-background/40 px-2 py-1.5 text-center">
      <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold truncate">
        {label}
      </p>
      <p className={cn("text-base font-bold tabular-nums leading-tight", valueTone)}>{value}</p>
    </div>
  );
}

function BreakdownCard({
  label,
  agg,
  t,
}: {
  label: string;
  agg: CvAggregate;
  t: (k: string, opts?: any) => string;
}) {
  return (
    <div className="rounded-xl border bg-card/60 p-3 space-y-2">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
        {label}
      </p>
      {agg.games === 0 ? (
        <p className="text-xs text-muted-foreground italic">{t("cv.noData")}</p>
      ) : (
        <div className="grid grid-cols-3 gap-1.5">
          <KpiTile label={t("cv.kpi.matches")} value={String(agg.games)} />
          <KpiTile label={t("cv.kpi.goals")} value={String(agg.goals)} tone="primary" />
          <KpiTile label={t("cv.kpi.assists")} value={String(agg.assists)} tone="amber" />
          <KpiTile label={t("cv.kpi.rating")} value={agg.ratingAvg.toFixed(2)} />
          <KpiTile label={t("cv.kpi.winRate")} value={`${agg.winPct}%`} />
          <KpiTile label={t("cv.kpi.mom")} value={String(agg.mom)} />
        </div>
      )}
    </div>
  );
}

function TopMatchTile({
  icon,
  title,
  match,
  metricLabel,
  metricValue,
}: {
  icon: React.ReactNode;
  title: string;
  match: NonNullable<CvTopMatch>;
  metricLabel: string;
  metricValue: string;
}) {
  const cls =
    match.ourGoals > match.oppGoals ? "win" : match.ourGoals < match.oppGoals ? "lose" : "draw";
  return (
    <div className="rounded-xl border bg-card/60 p-3 space-y-1">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1">
        {icon}
        {title}
      </p>
      <p className="text-sm font-medium truncate">{match.opponentName}</p>
      <p className="text-[11px] text-muted-foreground">
        {match.date ? new Date(match.date).toLocaleDateString(currentIntlLocale()) : "-"}
      </p>
      <div className="flex items-center gap-2 text-sm pt-1">
        <span className={cn("score-chip", cls)}>
          {match.ourGoals}-{match.oppGoals}
        </span>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {metricLabel}
        </span>
        <span className="font-mono font-bold text-base">{metricValue}</span>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// ShareableCv: versione brandizzata della carta. Renderizzata off-screen
// con inline styles (per evitare problemi con tailwind/dark theme nel
// clone DOM) e catturata da html-to-image.
// ──────────────────────────────────────────────────────────────────────────

function ShareableCv({
  displayName,
  initials,
  rolesAffinity,
  eaPersona,
  career,
  season,
  friendly,
  all,
  bestRating,
  bestGoals,
  t,
}: {
  displayName: string;
  initials: string;
  rolesAffinity?: string | null;
  eaPersona: string;
  career: CvCareer | null;
  season: CvAggregate;
  friendly: CvAggregate;
  all: CvAggregate;
  bestRating: CvTopMatch;
  bestGoals: CvTopMatch;
  t: (k: string, opts?: any) => string;
}) {
  const tile = (label: string, value: string, tone?: "primary" | "amber") => (
    <div
      style={{
        background: "rgba(15,23,42,0.6)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: "10px",
        padding: "8px 10px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontSize: "9px",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: "rgba(241,245,244,0.55)",
          fontWeight: 600,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "18px",
          fontWeight: 800,
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1.1,
          color:
            tone === "primary"
              ? "#c8f25a"
              : tone === "amber"
                ? "#ffc93c"
                : "rgba(241,245,244,0.95)",
        }}
      >
        {value}
      </div>
    </div>
  );

  const sectionHeader = (label: string) => (
    <div
      style={{
        fontSize: "10px",
        textTransform: "uppercase",
        letterSpacing: "0.18em",
        color: "rgba(241,245,244,0.55)",
        fontWeight: 600,
        marginBottom: "8px",
      }}
    >
      {label}
    </div>
  );

  return (
    <div
      style={{
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        background: "#0a1114",
        color: "#f1f5f4",
        padding: "32px",
        borderRadius: "24px",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      {/* Header brand */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <img
          src={ELEVENBASE_LOGO_URL}
          alt="ElevenBase"
          style={{
            height: "48px",
            width: "auto",
            filter: "brightness(0) invert(1)",
            display: "block",
          }}
        />
        <div
          style={{
            fontSize: "11px",
            textTransform: "uppercase",
            letterSpacing: "0.18em",
            color: "rgba(241,245,244,0.55)",
            fontWeight: 600,
          }}
        >
          {t("cv.shareWatermark")}
        </div>
      </div>

      {/* Identità */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          marginBottom: "22px",
          paddingBottom: "16px",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <div
          style={{
            width: "72px",
            height: "72px",
            borderRadius: "999px",
            background: "rgba(99,102,241,0.2)",
            border: "2px solid rgba(99,102,241,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "24px",
            fontWeight: 700,
            color: "rgba(165,180,252,1)",
          }}
        >
          {initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: "26px", fontWeight: 700, lineHeight: 1.1 }}>{displayName}</div>
          <div
            style={{
              fontSize: "12px",
              color: "rgba(241,245,244,0.55)",
              marginTop: "2px",
              fontFamily: "ui-monospace, SFMono-Regular, monospace",
            }}
          >
            {rolesAffinity ? `${rolesAffinity} · ` : ""}
            {eaPersona}
            {career?.proOverall ? ` · OVR ${career.proOverall}` : ""}
          </div>
          {career?.favoritePosition && (
            <div style={{ fontSize: "11px", color: "rgba(241,245,244,0.55)", marginTop: "2px" }}>
              {t("cv.favoritePosition")}: {career.favoritePosition}
            </div>
          )}
        </div>
      </div>

      {/* Carriera EA */}
      {career && career.gamesPlayed > 0 && (
        <div style={{ marginBottom: "20px" }}>
          {sectionHeader(t("cv.careerSection"))}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "8px" }}>
            {tile(t("cv.kpi.matches"), String(career.gamesPlayed))}
            {tile(t("cv.kpi.goals"), String(career.goals), "primary")}
            {tile(t("cv.kpi.assists"), String(career.assists), "amber")}
            {tile(t("cv.kpi.rating"), career.ratingAve.toFixed(2))}
            {tile(t("cv.kpi.winRate"), `${career.winRate.toFixed(0)}%`)}
            {tile(t("cv.kpi.mom"), String(career.mom))}
          </div>
        </div>
      )}

      {/* Stagione + Amichevole */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "12px",
          marginBottom: "20px",
        }}
      >
        {[
          { label: t("cv.section.season"), agg: season },
          { label: t("cv.section.friendly"), agg: friendly },
        ].map(({ label, agg }, idx) => (
          <div
            key={idx}
            style={{
              background: "rgba(15,23,42,0.5)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: "12px",
              padding: "12px",
            }}
          >
            {sectionHeader(label)}
            {agg.games === 0 ? (
              <div style={{ fontSize: "11px", color: "rgba(241,245,244,0.4)", fontStyle: "italic" }}>
                {t("cv.noData")}
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "6px" }}>
                {tile(t("cv.kpi.matches"), String(agg.games))}
                {tile(t("cv.kpi.goals"), String(agg.goals), "primary")}
                {tile(t("cv.kpi.assists"), String(agg.assists), "amber")}
                {tile(t("cv.kpi.rating"), agg.ratingAvg.toFixed(2))}
                {tile(t("cv.kpi.winRate"), `${agg.winPct}%`)}
                {tile(t("cv.kpi.mom"), String(agg.mom))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Top match */}
      {(bestRating || bestGoals) && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px",
            marginBottom: "20px",
          }}
        >
          {bestRating && (
            <div
              style={{
                background: "rgba(15,23,42,0.5)",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: "12px",
                padding: "12px",
              }}
            >
              {sectionHeader(t("cv.bestRating"))}
              <div style={{ fontSize: "13px", fontWeight: 600 }}>{bestRating.opponentName}</div>
              <div style={{ fontSize: "26px", fontWeight: 800, color: "#ffc93c", fontVariantNumeric: "tabular-nums" }}>
                {bestRating.rating.toFixed(2)}
              </div>
              <div style={{ fontSize: "11px", color: "rgba(241,245,244,0.55)" }}>
                {bestRating.ourGoals}-{bestRating.oppGoals}
                {bestRating.goals > 0 ? ` · ${bestRating.goals}G` : ""}
                {bestRating.assists > 0 ? ` · ${bestRating.assists}A` : ""}
              </div>
            </div>
          )}
          {bestGoals && bestGoals.goals > 0 && (
            <div
              style={{
                background: "rgba(15,23,42,0.5)",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: "12px",
                padding: "12px",
              }}
            >
              {sectionHeader(t("cv.bestGoals"))}
              <div style={{ fontSize: "13px", fontWeight: 600 }}>{bestGoals.opponentName}</div>
              <div style={{ fontSize: "26px", fontWeight: 800, color: "#c8f25a", fontVariantNumeric: "tabular-nums" }}>
                {bestGoals.goals}G
              </div>
              <div style={{ fontSize: "11px", color: "rgba(241,245,244,0.55)" }}>
                {bestGoals.ourGoals}-{bestGoals.oppGoals}
                {bestGoals.assists > 0 ? ` · ${bestGoals.assists}A` : ""}
                {` · rating ${bestGoals.rating.toFixed(2)}`}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div
        style={{
          fontSize: "10px",
          color: "rgba(241,245,244,0.4)",
          textAlign: "center",
          paddingTop: "12px",
          borderTop: "1px solid rgba(255,255,255,0.06)",
          letterSpacing: "0.05em",
        }}
      >
        {t("cv.shareFooter")}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Helpers: dialog, copia/condividi, waitForImages, dataUrlToBlob.
// Duplicati con PlayerRoleComparison: refactor in share-utils condivisi
// nel prossimo passaggio.
// ──────────────────────────────────────────────────────────────────────────

function ShareDialog({
  state,
  title,
  text,
  fileName,
  onClose,
  t,
}: {
  state: { dataUrl: string | null; error: string | null } | null;
  title: string;
  text: string;
  fileName: string;
  onClose: () => void;
  t: (k: string, opts?: any) => string;
}) {
  const [copied, setCopied] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    setCopied(false);
    setActionError(null);
  }, [state?.dataUrl, state?.error]);

  const handleDownload = () => {
    if (!state?.dataUrl) return;
    try {
      const link = document.createElement("a");
      link.href = state.dataUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "download_failed");
    }
  };

  const handleCopy = async () => {
    if (!state?.dataUrl) return;
    try {
      const blob = dataUrlToBlob(state.dataUrl);
      const nav: any = navigator;
      if (!nav?.clipboard?.write || typeof ClipboardItem === "undefined") {
        throw new Error("clipboard_unsupported");
      }
      await nav.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.error("[PlayerCv ShareDialog] copy failed", err);
      setActionError(t("cv.shareDialog.copyUnsupported"));
    }
  };

  const handleShare = async () => {
    if (!state?.dataUrl) return;
    try {
      const blob = dataUrlToBlob(state.dataUrl);
      const file = new File([blob], fileName, { type: "image/png" });
      const nav: any = navigator;
      if (nav?.canShare?.({ files: [file] })) {
        await nav.share({ files: [file], title, text });
      } else if (nav?.share) {
        await nav.share({ title, text, url: window.location.href });
      } else {
        setActionError(t("cv.shareDialog.shareUnsupported"));
      }
    } catch (err) {
      if (err instanceof Error && err.name !== "AbortError") {
        console.error("[PlayerCv ShareDialog] share failed", err);
        setActionError(err.message);
      }
    }
  };

  const canShareNative = typeof navigator !== "undefined" && typeof (navigator as any).share === "function";

  return (
    <Dialog open={!!state} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("cv.shareDialog.title")}</DialogTitle>
          <DialogDescription>{t("cv.shareDialog.desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-xl border bg-muted/30 p-2 min-h-[160px] flex items-center justify-center">
            {!state ? null : state.error ? (
              <div className="text-center space-y-1.5 py-6">
                <X className="h-6 w-6 text-rose-500 mx-auto" />
                <p className="text-sm text-rose-600 dark:text-rose-400 font-medium">
                  {t("cv.shareDialog.error")}
                </p>
                <p className="text-[11px] text-muted-foreground font-mono">{state.error}</p>
              </div>
            ) : state.dataUrl ? (
              <img src={state.dataUrl} alt="" className="max-w-full h-auto rounded-md shadow-md" />
            ) : (
              <div className="text-center space-y-2 py-6">
                <Loader2 className="h-6 w-6 text-muted-foreground mx-auto animate-spin" />
                <p className="text-sm text-muted-foreground">
                  {t("cv.shareDialog.generating")}
                </p>
              </div>
            )}
          </div>
          {actionError && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
              {actionError}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Button type="button" variant="default" onClick={handleDownload} disabled={!state?.dataUrl}>
              <Download className="h-4 w-4 mr-1.5" />
              {t("cv.shareDialog.download")}
            </Button>
            <Button type="button" variant="outline" onClick={handleCopy} disabled={!state?.dataUrl}>
              {copied ? (
                <Check className="h-4 w-4 mr-1.5 text-emerald-600" />
              ) : (
                <Copy className="h-4 w-4 mr-1.5" />
              )}
              {copied ? t("cv.shareDialog.copied") : t("cv.shareDialog.copy")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleShare}
              disabled={!state?.dataUrl || !canShareNative}
              title={canShareNative ? undefined : t("cv.shareDialog.shareUnsupported")}
            >
              <Share2 className="h-4 w-4 mr-1.5" />
              {t("cv.shareDialog.share")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

async function waitForImages(root: HTMLElement): Promise<void> {
  const imgs = Array.from(root.querySelectorAll("img"));
  if (imgs.length === 0) return;
  await Promise.all(
    imgs.map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      if ("decode" in img) return img.decode().catch(() => undefined);
      return new Promise<void>((resolve) => {
        (img as HTMLImageElement).addEventListener("load", () => resolve(), { once: true });
        (img as HTMLImageElement).addEventListener("error", () => resolve(), { once: true });
      });
    }),
  );
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [meta, base64] = dataUrl.split(",");
  const mimeMatch = meta.match(/:(.*?);/);
  const mime = mimeMatch?.[1] || "image/png";
  const binary = atob(base64);
  const arr = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i);
  return new Blob([arr], { type: mime });
}
